/**
 * The URL of a category page, in one locale.
 *
 * **This lives in its own module, apart from the rest of `services/categories`,
 * on purpose.** It is a pure path helper — it needs no catalogue — but the
 * module it used to share reaches `getAllProducts`, and through it the eager
 * glob over every strain's `.svx`. The header's category nav and the mobile
 * menu both call this, and the header is in the root layout, so that one import
 * pulled the entire strain corpus into the client bundle of every page on the
 * site. `services/categories` re-exports it, so server-side callers are
 * unaffected; anything that runs in the browser should import it from here.
 */

import { permalinkToPath } from '$lib/format';
import { COMMERCE_LOCALES, prefixPath, type Locale } from '$lib/i18n';

/**
 * The URL of a category page in a given locale.
 *
 * `en` and `th` have their own WooCommerce snapshot, so the live permalink is
 * authoritative: WPML translated those slugs, and
 * `/th/product-category/เมล็ดพันธุ์เพศเมีย` is the indexed URL — not the English
 * slug with a `/th` prefix.
 *
 * The other six locales have no snapshot. `commerceLocale()` resolves them to
 * the **English** catalogue, whose permalink carries no locale prefix, so using
 * it directly emitted `/product-category/feminised-seeds` on a Japanese page —
 * which is how the entire header menu, the footer and the language switcher sent
 * visitors on those locales back to the English site from any translated
 * page. Their pages are prerendered at the English slug under the locale prefix,
 * so that is what gets built here.
 *
 * Every category link must resolve through this. Prefixing by hand is the bug
 * this function exists to prevent.
 */
export function categoryPath(locale: Locale, englishSlug: string, permalink?: string): string {
	if (permalink && (COMMERCE_LOCALES as readonly string[]).includes(locale)) {
		return permalinkToPath(permalink);
	}
	return prefixPath(`/product-category/${decodeURIComponent(englishSlug)}`, locale);
}
